import {
  Binary,
  Bool,
  type DataType,
  DateDay,
  Decimal,
  Field,
  FixedSizeBinary,
  Float16,
  Float32,
  Float64,
  Int16,
  Int32,
  Int64,
  Int8,
  List,
  Map_,
  Null,
  Struct,
  TimeMicrosecond,
  TimeMillisecond,
  TimeNanosecond,
  Timestamp,
  TimeUnit,
  Uint16,
  Uint32,
  Uint64,
  Uint8,
  Utf8,
} from "apache-arrow";
import {
  type ParquetParsers,
  type SchemaElement,
  type SchemaTree,
} from "hyparquet";
import { isListLike, isMapLike } from "hyparquet/src/schema.js";

import { READER_PARSERS } from "../parquet/ParquetReader";
import { mapKeyParser } from "../parquet/schema";

import { type SubtreeSelection } from "./projection";
import { fieldNotInFileError } from "./ReadPlanExecutionError";

/**
 * How a Parquet column's schema maps to an Arrow type, and how the values
 * hyparquet assembles for it become that type's values.
 *
 * Most Parquet types map to an Arrow type that holds each stored value
 * unchanged, the type pyarrow also reads them as. Two map to a type that
 * holds the value hyparquet assembles, which can differ from the stored
 * one: UUID to its hyphenated string, and a MAP to the entries of the
 * JavaScript object hyparquet assembles, so a repeated key keeps its last
 * value, integer-like keys come first in ascending order, and an entry
 * keyed `__proto__` is lost. JSON reads as its stored text, marked with
 * the `arrow.json` extension metadata, and GEOMETRY and GEOGRAPHY as
 * their stored bytes.
 *
 * Fields are built from the Parquet schema alone, so they carry none of the
 * field metadata of an Arrow schema stored in the footer entry
 * `ARROW:schema`, such as a `geoarrow.wkb` extension name on a GEOMETRY
 * column. pyarrow writes that entry and restores its metadata on read.
 */

/**
 * The parsers a read-plan decode reads with: {@link READER_PARSERS}, which
 * keep timestamps and dates as the integers of their unit, with VARIANT
 * columns kept encoded, JSON as its stored text, GEOMETRY and GEOGRAPHY as
 * their stored bytes, and each DECIMAL value as its unscaled integer.
 * {@link arrowColumn} keeps that integer as the unscaled value of an Arrow
 * decimal of the column's precision and scale; a DECIMAL timestamp is
 * scaled to nanoseconds in integer arithmetic when rows are matched to the
 * window.
 */
export const READ_PLAN_PARSERS: ParquetParsers = {
  ...READER_PARSERS,
  variantFromParts: (fields: Record<string, unknown>) => fields,
  decimalFromUnscaled: (unscaled: bigint) => unscaled,
  jsonFromBytes: (bytes: Uint8Array): unknown =>
    READER_PARSERS.stringFromBytes(bytes),
  geometryFromBytes: (bytes: Uint8Array) => bytes,
  geographyFromBytes: (bytes: Uint8Array) => bytes,
};

/**
 * The scale hyparquet reads the DECIMAL leaf `element` with: its `scale`,
 * else its logical type's, else 0.
 */
export function decimalScale(element: SchemaElement): number {
  const { scale, logical_type: logical } = element;
  return scale || (logical?.type === "DECIMAL" ? logical.scale : 0) || 0;
}

/** One projected top-level column in Arrow form. */
export interface ArrowColumn {
  /** The output field, nullable, holding only the selected fields. */
  field: Field;
  /**
   * `field`'s type with every Timestamp and Date replaced by the integer
   * type that holds its values. Arrow's Timestamp and Date builders take
   * epoch milliseconds, so values are built as integers and relabeled.
   */
  storageType: DataType;
  /**
   * Turns one row's value as hyparquet assembles it into the value a
   * builder of `storageType` takes.
   */
  adapt: (value: unknown) => unknown;
}

/**
 * The Arrow column for the top-level Parquet column `tree`, keeping only
 * what `selection` names below it. Paths name struct fields only: list
 * levels are crossed without a name, as ingestion registers them, and a map
 * is kept whole whatever `selection` names below it. Throws
 * `field-not-in-file`, naming topic partition `topicPartId`, when
 * `selection` names a field the column lacks, or one below a leaf.
 */
export function arrowColumn(
  tree: SchemaTree,
  selection: SubtreeSelection,
  topicPartId: string,
): ArrowColumn {
  const mapped = mapNode(tree, selection, {
    path: [tree.element.name],
    topicPartId,
    inVariant: false,
  });
  return {
    field: new Field(tree.element.name, mapped.type, true, mapped.metadata),
    storageType: mapped.storage,
    adapt: mapped.adapt ?? identity,
  };
}

/**
 * The children of `node` when it maps to an Arrow struct of them: a group
 * that is not repeated, and not a list, map or VARIANT. Undefined otherwise.
 */
export function structChildren(node: SchemaTree): SchemaTree[] | undefined {
  const { children, element } = node;
  if (
    children.length === 0 ||
    element.repetition_type === "REPEATED" ||
    element.logical_type?.type === "VARIANT" ||
    isMapLike(node) ||
    isListLike(node) ||
    (isListAnnotated(element) && children.length === 1)
  ) {
    return undefined;
  }
  return children;
}

interface Mapped {
  type: DataType;
  storage: DataType;
  /** The field metadata of the node's own field; none when absent. */
  metadata?: Map<string, string>;
  /** Absent when hyparquet's value is already what a builder takes. */
  adapt?: (value: unknown) => unknown;
}

interface Context {
  /** Names from the column root to the node, for errors. */
  path: string[];
  /** Names the partition in errors. */
  topicPartId: string;
  /**
   * Whether the node is inside a VARIANT group, where a BYTE_ARRAY leaf not
   * annotated as a string maps to Binary.
   */
  inVariant: boolean;
}

/**
 * The field metadata of Arrow's canonical JSON extension type, which pyarrow
 * gives a Parquet JSON column.
 */
const ARROW_JSON_FIELD_METADATA = new Map([
  ["ARROW:extension:name", "arrow.json"],
  ["ARROW:extension:metadata", ""],
]);

function identity(value: unknown): unknown {
  return value;
}

function isNull(value: unknown): value is null | undefined {
  return value === null || value === undefined;
}

/**
 * A node as hyparquet assembles it. A REPEATED node outside a LIST or MAP
 * group is a required list of its own type, per the Parquet
 * backward-compatibility rules; the list and map branches map their
 * repeated levels themselves.
 */
function mapNode(
  node: SchemaTree,
  selection: SubtreeSelection,
  context: Context,
): Mapped {
  const inner = mapUnrepeated(node, selection, context);
  if (node.element.repetition_type !== "REPEATED") {
    return inner;
  }
  // hyparquet gives null for an empty repeated primitive; the list is
  // required, so it is empty.
  return listOf(inner, (value) => value ?? []);
}

/** A node's type, ignoring whether the node itself repeats. */
function mapUnrepeated(
  node: SchemaTree,
  selection: SubtreeSelection,
  context: Context,
): Mapped {
  if (node.children.length === 0) {
    assertNothingBelow(selection, context);
    return mapLeaf(node.element, context);
  }
  if (node.element.logical_type?.type === "VARIANT") {
    return mapStruct(node.children, "all", {
      ...context,
      inVariant: true,
    });
  }
  if (isMapLike(node)) {
    return mapMap(node, context);
  }
  if (isListLike(node)) {
    return mapList(node, selection, context);
  }
  if (isListAnnotated(node.element) && node.children.length === 1) {
    // A LIST whose repeated group holds several fields: the group is the
    // element. hyparquet assembles the outer group as a struct holding it.
    const repeated = node.children[0];
    const element = mapStruct(repeated.children, selection, context);
    const name = repeated.element.name;
    return listOf(element, (value) =>
      isNull(value) ? value : (value as Record<string, unknown>)[name],
    );
  }
  return mapStruct(node.children, selection, context);
}

function mapList(
  node: SchemaTree,
  selection: SubtreeSelection,
  context: Context,
): Mapped {
  const repeated = node.children[0];
  if (repeated.children.length === 0) {
    // Two-level list: the repeated primitive is the element. hyparquet gives
    // null for an empty list and undefined for a null one.
    assertNothingBelow(selection, context);
    return listOf(mapLeaf(repeated.element, context), (value) =>
      value === null ? [] : value,
    );
  }
  const child = repeated.children[0];
  if (
    repeated.element.name === "array" ||
    repeated.element.name === `${node.element.name}_tuple`
  ) {
    // Legacy two-level list of one-field groups: the repeated group is the
    // element, but hyparquet unwraps it to its field, so wrap it back.
    const element = mapStruct([child], selection, context);
    const name = child.element.name;
    const adaptStruct = element.adapt ?? identity;
    return listOf({
      ...element,
      adapt: (value) => adaptStruct({ [name]: value }),
    });
  }
  return listOf(mapNode(child, selection, context));
}

function mapMap(node: SchemaTree, context: Context): Mapped {
  const [keyNode, valueNode] = node.children[0].children;
  const key = mapNode(keyNode, "all", {
    ...context,
    path: [...context.path, keyNode.element.name],
  });
  const value = mapNode(valueNode, "all", {
    ...context,
    path: [...context.path, valueNode.element.name],
  });
  const entries = (keyType: DataType, valueType: DataType) =>
    new Map_(
      new Field(
        "entries",
        new Struct<{ key: DataType; value: DataType }>([
          new Field("key", keyType, false, key.metadata),
          new Field("value", valueType, true, value.metadata),
        ]),
        false,
      ),
      false,
    );
  const parseKey = mapKeyParser(keyNode.element) ?? identity;
  const adaptKey = key.adapt ?? identity;
  const adaptValue = value.adapt ?? identity;
  return {
    type: entries(key.type, value.type),
    storage: entries(key.storage, value.storage),
    // hyparquet assembles a map as an object keyed by each key's string
    // form; the Arrow builder takes a Map, whose keys keep their type.
    adapt: (map) =>
      isNull(map)
        ? map
        : new Map(
            Object.entries(map as Record<string, unknown>).map(
              ([entryKey, entryValue]) => [
                adaptKey(parseKey(entryKey)),
                adaptValue(entryValue),
              ],
            ),
          ),
  };
}

function mapStruct(
  children: SchemaTree[],
  selection: SubtreeSelection,
  context: Context,
): Mapped {
  if (selection !== "all") {
    for (const name of selection.keys()) {
      if (!children.some((child) => child.element.name === name)) {
        throw missingFieldError(context, name);
      }
    }
  }
  const mapped = children.flatMap((child) => {
    const name = child.element.name;
    const childSelection = selection === "all" ? "all" : selection.get(name);
    if (childSelection === undefined) {
      return [];
    }
    return [
      {
        name,
        ...mapNode(child, childSelection, {
          ...context,
          path: [...context.path, name],
        }),
      },
    ];
  });
  const adapted = mapped.filter((child) => child.adapt !== undefined);
  return {
    type: new Struct(
      mapped.map(
        (child) => new Field(child.name, child.type, true, child.metadata),
      ),
    ),
    storage: new Struct(
      mapped.map((child) => new Field(child.name, child.storage, true)),
    ),
    // The builder reads only its own fields, so an unselected field needs no
    // removing; only fields whose values change are rewritten.
    adapt:
      adapted.length === 0
        ? undefined
        : (value) => {
            if (isNull(value)) {
              return value;
            }
            const struct = { ...(value as Record<string, unknown>) };
            for (const child of adapted) {
              struct[child.name] = child.adapt!(struct[child.name]);
            }
            return struct;
          },
  };
}

function listOf(element: Mapped, unwrap?: (value: unknown) => unknown): Mapped {
  const adaptElement = element.adapt;
  const adaptList =
    adaptElement === undefined
      ? unwrap
      : (value: unknown) => {
          const list = unwrap === undefined ? value : unwrap(value);
          return isNull(list)
            ? list
            : Array.from(list as ArrayLike<unknown>, adaptElement);
        };
  return {
    type: new List(new Field("item", element.type, true, element.metadata)),
    storage: new List(new Field("item", element.storage, true)),
    adapt: adaptList,
  };
}

/** Throws when a selection names a field below a leaf. */
function assertNothingBelow(
  selection: SubtreeSelection,
  context: Context,
): void {
  if (selection !== "all") {
    const [name] = selection.keys();
    throw missingFieldError(context, name);
  }
}

function isListAnnotated(element: SchemaElement): boolean {
  return (
    element.converted_type === "LIST" || element.logical_type?.type === "LIST"
  );
}

/**
 * A leaf's type, matching the value hyparquet converts the leaf to with
 * {@link READ_PLAN_PARSERS}. A leaf may carry a logical type beside the
 * converted type it corresponds to; the two give the same value, and a
 * logical TIMESTAMP is checked first to keep its UTC adjustment.
 */
function mapLeaf(element: SchemaElement, context: Context): Mapped {
  const { type, converted_type: converted, logical_type: logical } = element;
  const same = (arrowType: DataType, adapt?: Mapped["adapt"]): Mapped => ({
    type: arrowType,
    storage: arrowType,
    adapt,
  });

  if (logical?.type === "NULL") {
    return same(new Null());
  }
  if (
    context.inVariant &&
    type === "BYTE_ARRAY" &&
    converted !== "UTF8" &&
    logical?.type !== "STRING"
  ) {
    return same(new Binary());
  }
  if (converted === "DECIMAL" || logical?.type === "DECIMAL") {
    // pyarrow reads a precision up to 38 as Decimal128 and up to 76 as
    // Decimal256, and refuses a wider one.
    const precision =
      element.precision ||
      (logical?.type === "DECIMAL" ? logical.precision : undefined);
    if (precision === undefined || precision < 1 || precision > 76) {
      throw new Error(
        `Field "${context.path.join(".")}" is a Parquet DECIMAL of precision ${precision ?? "(none)"}, outside the 1 to 76 digits an Arrow decimal holds`,
      );
    }
    const bitWidth = precision <= 38 ? 128 : 256;
    return same(
      new Decimal(decimalScale(element), precision, bitWidth),
      (value) =>
        isNull(value) ? value : decimalWords(value as bigint, bitWidth),
    );
  }
  if (converted === undefined && type === "INT96") {
    return {
      type: new Timestamp(TimeUnit.NANOSECOND, null),
      storage: new Int64(),
    };
  }
  if (logical?.type === "TIMESTAMP") {
    return {
      type: new Timestamp(
        arrowTimeUnit(logical.unit),
        logical.isAdjustedToUTC ? "UTC" : null,
      ),
      storage: new Int64(),
    };
  }
  if (converted === "TIMESTAMP_MILLIS" || converted === "TIMESTAMP_MICROS") {
    // A converted timestamp is adjusted to UTC by definition.
    return {
      type: new Timestamp(
        converted === "TIMESTAMP_MILLIS"
          ? TimeUnit.MILLISECOND
          : TimeUnit.MICROSECOND,
        "UTC",
      ),
      storage: new Int64(),
    };
  }
  if (converted === "DATE" || logical?.type === "DATE") {
    return { type: new DateDay(), storage: new Int32() };
  }
  if (converted === "JSON" || logical?.type === "JSON") {
    return { ...same(new Utf8()), metadata: ARROW_JSON_FIELD_METADATA };
  }
  if (converted === "BSON" || logical?.type === "BSON") {
    return same(new Binary());
  }
  if (converted === "INTERVAL") {
    return same(new FixedSizeBinary(12));
  }
  if (logical?.type === "GEOMETRY" || logical?.type === "GEOGRAPHY") {
    return same(new Binary());
  }
  if (
    logical?.type === "UUID" ||
    converted === "UTF8" ||
    logical?.type === "STRING" ||
    type === "BYTE_ARRAY"
  ) {
    return same(new Utf8());
  }
  if (logical?.type === "FLOAT16") {
    return same(new Float16());
  }
  if (
    converted === "TIME_MILLIS" ||
    (logical?.type === "TIME" && logical.unit === "MILLIS")
  ) {
    return same(new TimeMillisecond());
  }
  if (
    converted === "TIME_MICROS" ||
    (logical?.type === "TIME" && logical.unit === "MICROS")
  ) {
    return same(new TimeMicrosecond());
  }
  if (logical?.type === "TIME") {
    return same(new TimeNanosecond());
  }
  const integer = integerType(element);
  if (integer !== undefined) {
    return same(integer);
  }
  switch (type) {
    case "BOOLEAN":
      return same(new Bool());
    case "FLOAT":
      return same(new Float32());
    case "DOUBLE":
      return same(new Float64());
    case "FIXED_LEN_BYTE_ARRAY":
      return same(new FixedSizeBinary(element.type_length ?? 0));
    default:
      throw new Error(
        `Field "${context.path.join(".")}" has Parquet type ${type ?? "(none)"}, which the topic data reader does not recognize`,
      );
  }
}

/**
 * The value Arrow's decimal builder takes for the unscaled integer
 * `unscaled`: its two's complement in `bitWidth` bits, split into 32-bit
 * words, least significant first.
 */
function decimalWords(unscaled: bigint, bitWidth: number): Uint32Array {
  let bits = BigInt.asUintN(bitWidth, unscaled);
  const words = new Uint32Array(bitWidth / 32);
  for (let index = 0; index < words.length; index += 1) {
    words[index] = Number(bits & 0xffffffffn);
    bits >>= 32n;
  }
  return words;
}

/** The integer type of an INT32 or INT64 leaf, or undefined for another leaf. */
function integerType(element: SchemaElement): DataType | undefined {
  const { type, converted_type: converted, logical_type: logical } = element;
  if (type !== "INT32" && type !== "INT64") {
    return undefined;
  }
  let bitWidth = type === "INT32" ? 32 : 64;
  let signed = true;
  if (logical?.type === "INTEGER") {
    bitWidth = logical.bitWidth;
    signed = logical.isSigned;
  } else if (converted?.startsWith("INT_") || converted?.startsWith("UINT_")) {
    signed = converted.startsWith("INT_");
    bitWidth = Number(converted.slice(converted.indexOf("_") + 1));
  }
  switch (bitWidth) {
    case 8:
      return signed ? new Int8() : new Uint8();
    case 16:
      return signed ? new Int16() : new Uint16();
    case 32:
      return signed ? new Int32() : new Uint32();
    default:
      return signed ? new Int64() : new Uint64();
  }
}

function arrowTimeUnit(unit: "MILLIS" | "MICROS" | "NANOS"): TimeUnit {
  switch (unit) {
    case "MILLIS":
      return TimeUnit.MILLISECOND;
    case "MICROS":
      return TimeUnit.MICROSECOND;
    case "NANOS":
      return TimeUnit.NANOSECOND;
  }
}

/** The `field-not-in-file` error for the field `name` below `context`'s node. */
function missingFieldError(context: Context, name: string) {
  return fieldNotInFileError({
    format: "Parquet",
    topicPartId: context.topicPartId,
    fieldPath: [...context.path, name],
  });
}
